import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { queryKeys, useApiMutation, useApiQuery } from '../../api/hooks';
import type {
  ChangeMachineStatusRequest,
  CompleteMaintenanceJobRequest,
  CreateMaintenanceJobRequest,
  MachineAlertResponse,
  MachineCounterResponse,
  MachineResponse,
  MachineStatusChangeResponse,
  MaintenanceJobResponse,
  MaintenanceKind,
  RegisterMachineRequest,
  ServiceResponse,
} from '../../api/types';
import { AsyncSection } from '../../components/AsyncSection';
import { PageHeader } from '../../components/PageHeader';
import { shortDate } from '../../i18n/format';
import { PanelHeading } from '../money/PanelHeading';
import { ErrorState } from '../../components/states/StateViews';
import { useAuthStore } from '../../auth/authStore';
import { useLocalisedName } from '../../i18n/localisedName';
import { useBranchPicker } from '../scheduling/useBranchPicker';
import { MachineDetailPanel } from './MachineDetailPanel';

const JOB_KINDS: readonly MaintenanceKind[] = [
  'SCHEDULED',
  'BREAKDOWN',
  'INSPECTION',
  'CALIBRATION',
  'INSTALLATION',
];

/**
 * The fleet (FR-806…809, DEC-035).
 *
 * Alerts first, for the reason the inventory screen puts them first: the question this is opened
 * with is "is anything about to cost us money or a Saturday of cancellations".
 *
 * Taking a machine out of service is the one action here with consequences beyond this page, so it
 * asks for a reason and then says plainly how many booked patients it stranded and where the queue
 * is. A button that silently emptied four appointments into a list nobody mentioned would be worse
 * than no button.
 */
export function MachinesPage() {
  const { t, i18n } = useTranslation();
  const { name } = useLocalisedName();
  const { clinicId, branchId, branches, choose } = useBranchPicker();
  const permissions = useAuthStore((state) => state.permissions);
  const canManage = permissions.has('MACHINE_MANAGE');
  const canMaintain = permissions.has('MACHINE_MAINTENANCE_MANAGE');

  const [openMachine, setOpenMachine] = useState<string | null>(null);
  const [lastChange, setLastChange] = useState<MachineStatusChangeResponse | null>(null);

  const branchKey = branchId ?? '';
  const enabled = Boolean(clinicId && branchId);

  const alerts = useApiQuery<MachineAlertResponse[]>(
    queryKeys.machineAlerts(clinicId ?? '', branchKey),
    `/clinics/${clinicId}/machines/alerts?branchId=${branchId}`,
    { enabled },
  );
  const machines = useApiQuery<MachineResponse[]>(
    queryKeys.machines(clinicId ?? '', branchKey),
    `/clinics/${clinicId}/machines?branchId=${branchId}`,
    { enabled },
  );
  const jobs = useApiQuery<MaintenanceJobResponse[]>(
    queryKeys.maintenanceJobs(clinicId ?? '', `open:${branchKey}`),
    `/clinics/${clinicId}/machines/maintenance?branchId=${branchId}`,
    { enabled },
  );
  const counters = useApiQuery<MachineCounterResponse[]>(
    queryKeys.machineCounters(clinicId ?? '', branchKey),
    `/clinics/${clinicId}/machines/counters?branchId=${branchId}`,
    { enabled },
  );
  const services = useApiQuery<ServiceResponse[]>(
    queryKeys.services(clinicId ?? ''),
    `/clinics/${clinicId}/catalog/services`,
    { enabled: Boolean(clinicId) },
  );

  const fleetKeys = [
    queryKeys.machines(clinicId ?? '', branchKey),
    queryKeys.machineAlerts(clinicId ?? '', branchKey),
    queryKeys.maintenanceJobs(clinicId ?? '', `open:${branchKey}`),
    queryKeys.machineCounters(clinicId ?? '', branchKey),
  ];

  const register = useApiMutation<MachineResponse, RegisterMachineRequest>(
    (body) => ({ path: `/clinics/${clinicId}/machines`, options: { method: 'POST', body } }),
    { invalidates: fleetKeys },
  );

  const changeStatus = useApiMutation<
    MachineStatusChangeResponse,
    ChangeMachineStatusRequest & { readonly id: string }
  >(
    ({ id, ...body }) => ({
      path: `/clinics/${clinicId}/machines/${id}/status`,
      options: { method: 'POST', body },
    }),
    {
      invalidates: fleetKeys,
      onSuccess: () => setLastChange(null),
    },
  );

  const planJob = useApiMutation<MaintenanceJobResponse, CreateMaintenanceJobRequest>(
    (body) => ({ path: `/clinics/${clinicId}/machines/maintenance`, options: { method: 'POST', body } }),
    { invalidates: fleetKeys },
  );

  const completeJob = useApiMutation<
    MaintenanceJobResponse,
    CompleteMaintenanceJobRequest & { readonly id: string }
  >(
    ({ id, ...body }) => ({
      path: `/clinics/${clinicId}/machines/maintenance/${id}/complete`,
      options: { method: 'POST', body },
    }),
    { invalidates: fleetKeys },
  );

  if (!clinicId) {
    return <PageHeader title={t('nav.items.machines')} description={t('home.noClinic')} />;
  }

  const takeOutOfService = (machine: MachineResponse) => {
    const reason = window.prompt(t('machines.statusReason') ?? '');
    if (!reason) {
      return;
    }
    changeStatus.mutate(
      { id: machine.id ?? '', status: 'MAINTENANCE', reason },
      { onSuccess: (result) => setLastChange(result) },
    );
  };

  return (
    <>
      <PageHeader title={t('nav.items.machines')} description={t('machines.description')} />

      <div className="filters">
        <label className="field">
          <span>{t('board.branch')}</span>
          <select value={branchId ?? ''} onChange={(event) => choose(event.target.value)}>
            {branches.map((branch) => (
              <option key={branch.id} value={branch.id}>
                {name(branch, branch.code)}
              </option>
            ))}
          </select>
        </label>
      </div>

      {lastChange ? (
        <section className="panel panel--notice" role="status">
          <p>
            {lastChange.strandedAppointments && lastChange.strandedAppointments > 0
              ? t('machines.stranded', { count: lastChange.strandedAppointments })
              : t('machines.strandedNone')}
          </p>
          {lastChange.worklistId ? <a href="/worklists">{t('machines.openWorklist')}</a> : null}
        </section>
      ) : null}

      <AsyncSection query={alerts}>
        {(rows) => (
          <section className="panel">
            <PanelHeading title={t('machines.alerts')} hint={t('machines.alertsHint')} />
            {rows.length === 0 ? (
              <p className="muted">{t('machines.noAlerts')}</p>
            ) : (
              <ul className="totals-list">
                {rows.map((alert) => (
                  <li key={`${alert.kind}:${alert.machineId}`}>
                    <span className={`pill pill--${(alert.kind ?? '').toLowerCase()}`}>
                      {t(`machines.alertKinds.${alert.kind}`)}
                    </span>
                    <strong>{alert.machineName}</strong>
                    <span className="muted">
                      {alert.on
                        ? t('machines.alertOn', { date: shortDate(alert.on, i18n.language), days: alert.days })
                        : (alert.detail ?? '')}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}
      </AsyncSection>

      <AsyncSection query={machines} skeletonRows={4}>
        {(rows) => (
          <section className="panel">
            <PanelHeading title={t('machines.registry')} hint={t('machines.registryHint')} />
            {rows.length === 0 ? (
              <p className="muted">{t('machines.none')}</p>
            ) : (
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th>{t('machines.code')}</th>
                      <th>{t('machines.machine')}</th>
                      <th>{t('machines.serial')}</th>
                      <th>{t('sales.status')}</th>
                      <th>{t('machines.warranty')}</th>
                      <th>{t('machines.treatments')}</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((machine) => (
                      <tr
                        key={machine.id}
                        className={machine.status !== 'ACTIVE' ? 'row--warning' : undefined}
                      >
                        <td>{machine.code}</td>
                        <td>{name(machine, machine.code ?? '')}</td>
                        <td className="muted">{machine.serialNumber ?? '—'}</td>
                        <td>
                          <span className={`pill pill--${(machine.status ?? '').toLowerCase()}`}>
                            {t(`machines.statuses.${machine.status}`)}
                          </span>
                          {machine.currentDowntime ? (
                            <span className="muted"> {machine.currentDowntime.reason}</span>
                          ) : null}
                        </td>
                        <td className="muted">
                          {machine.warrantyEndsOn
                            ? `${machine.warrantyEndsOn}${machine.underWarranty ? '' : ' ⚠'}`
                            : '—'}
                        </td>
                        <td className="muted">{machine.compatibleServices?.length ?? 0}</td>
                        <td className="actions">
                          <button
                            type="button"
                            onClick={() =>
                              setOpenMachine(openMachine === machine.id ? null : (machine.id ?? null))
                            }
                          >
                            {t('machines.details')}
                          </button>
                          {canManage && machine.status === 'ACTIVE' ? (
                            <button type="button" onClick={() => takeOutOfService(machine)}>
                              {t('machines.takeDown')}
                            </button>
                          ) : null}
                          {canManage && machine.status === 'MAINTENANCE' ? (
                            <button
                              type="button"
                              className="primary"
                              onClick={() =>
                                changeStatus.mutate({
                                  id: machine.id ?? '',
                                  status: 'ACTIVE',
                                  reason: t('machines.backInService'),
                                })
                              }
                            >
                              {t('machines.bringBack')}
                            </button>
                          ) : null}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {changeStatus.isError ? <ErrorState error={changeStatus.error} /> : null}
          </section>
        )}
      </AsyncSection>

      {openMachine ? (
        <MachineDetailPanel
          clinicId={clinicId}
          machineId={openMachine}
          services={services.data ?? []}
          canManage={canManage}
          invalidates={fleetKeys}
        />
      ) : null}

      <AsyncSection query={jobs}>
        {(rows) => (
          <section className="panel">
            <PanelHeading title={t('machines.openJobs')} hint={t('machines.openJobsHint')} />
            {rows.length === 0 ? (
              <p className="muted">{t('machines.noJobs')}</p>
            ) : (
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th>{t('machines.machine')}</th>
                      <th>{t('machines.jobKind')}</th>
                      <th>{t('machines.jobTitle')}</th>
                      <th>{t('machines.vendor')}</th>
                      <th>{t('sales.status')}</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((job) => (
                      <tr key={job.id}>
                        <td>{job.machineName}</td>
                        <td>{t(`machines.jobKinds.${job.kind}`)}</td>
                        <td>{job.title}</td>
                        <td className="muted">{job.vendor ?? '—'}</td>
                        <td>
                          <span className={`pill pill--${(job.status ?? '').toLowerCase()}`}>
                            {t(`machines.jobStatuses.${job.status}`)}
                          </span>
                        </td>
                        <td className="actions">
                          {canMaintain ? (
                            <button
                              type="button"
                              className="primary"
                              onClick={() => {
                                const cost = window.prompt(t('machines.costPrompt') ?? '', '0');
                                if (cost === null) {
                                  return;
                                }
                                completeJob.mutate({
                                  id: job.id ?? '',
                                  cost:
                                    Number(cost) > 0
                                      ? { amount: Number(cost).toFixed(2), currency: 'EGP' }
                                      : undefined,
                                  outcomeNote: window.prompt(t('machines.outcomePrompt') ?? '') ?? undefined,
                                  returnToService: true,
                                });
                              }}
                            >
                              {t('machines.completeJob')}
                            </button>
                          ) : null}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {completeJob.isError ? <ErrorState error={completeJob.error} /> : null}
          </section>
        )}
      </AsyncSection>

      {canMaintain ? (
        <section className="panel">
          <PanelHeading title={t('machines.planJob')} hint={t('machines.planJobHint')} />
          <form
            className="form-grid"
            onSubmit={(event) => {
              event.preventDefault();
              const form = new FormData(event.currentTarget);
              const from = String(form.get('scheduledFrom') ?? '');
              planJob.mutate({
                machineId: String(form.get('machineId')),
                kind: String(form.get('kind')) as MaintenanceKind,
                title: String(form.get('title')),
                vendor: String(form.get('vendor') ?? '') || undefined,
                scheduledFrom: from ? new Date(from).toISOString() : undefined,
              });
              event.currentTarget.reset();
            }}
          >
            <label className="field">
              <span>{t('machines.machine')}</span>
              <select name="machineId" required>
                {(machines.data ?? []).map((machine) => (
                  <option key={machine.id} value={machine.id}>
                    {name(machine, machine.code ?? '')}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>{t('machines.jobKind')}</span>
              <select name="kind" defaultValue="SCHEDULED">
                {JOB_KINDS.map((kind) => (
                  <option key={kind} value={kind}>
                    {t(`machines.jobKinds.${kind}`)}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>{t('machines.jobTitle')}</span>
              <input name="title" required maxLength={200} />
            </label>
            <label className="field">
              <span>{t('machines.vendor')}</span>
              <input name="vendor" />
            </label>
            <label className="field">
              <span>{t('machines.scheduledFrom')}</span>
              <input name="scheduledFrom" type="datetime-local" />
            </label>
            {planJob.isError ? <ErrorState error={planJob.error} /> : null}
            <button type="submit" className="primary" disabled={planJob.isPending}>
              {t('common.save')}
            </button>
          </form>
        </section>
      ) : null}

      <AsyncSection query={counters}>
        {(rows) => (
          <section className="panel">
            <PanelHeading title={t('machines.counters')} hint={t('machines.countersHint')} />
            {rows.length === 0 ? (
              <p className="muted">{t('machines.none')}</p>
            ) : (
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th>{t('machines.machine')}</th>
                      <th>{t('machines.sessions')}</th>
                      <th>{t('machines.pulses')}</th>
                      <th>{t('machines.lifetimePulses')}</th>
                      <th>{t('machines.downtime')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <tr key={row.machineId}>
                        <td>{row.machineName}</td>
                        <td>{row.sessions}</td>
                        <td>{row.pulses}</td>
                        <td className="muted">{row.lifetimePulses}</td>
                        <td className="muted">
                          {t('machines.downtimeDays', {
                            days: ((row.downtimeMinutes ?? 0) / 1440).toFixed(1),
                          })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        )}
      </AsyncSection>

      {canManage ? (
        <section className="panel">
          <PanelHeading title={t('machines.register')} hint={t('machines.registerHint')} />
          <form
            className="form-grid"
            onSubmit={(event) => {
              event.preventDefault();
              const form = new FormData(event.currentTarget);
              register.mutate({
                branchId: branchId ?? '',
                code: String(form.get('code')),
                nameEn: String(form.get('nameEn') ?? '') || undefined,
                nameAr: String(form.get('nameAr') ?? '') || undefined,
                manufacturer: String(form.get('manufacturer') ?? '') || undefined,
                model: String(form.get('model') ?? '') || undefined,
                serialNumber: String(form.get('serialNumber') ?? '') || undefined,
                supplier: String(form.get('supplier') ?? '') || undefined,
                warrantyEndsOn: String(form.get('warrantyEndsOn') ?? '') || undefined,
                baselinePulses: Number(form.get('baselinePulses') ?? 0) || undefined,
                compatibleServiceIds: [],
              });
              event.currentTarget.reset();
            }}
          >
            <label className="field">
              <span>{t('machines.code')}</span>
              <input name="code" required maxLength={48} />
            </label>
            <label className="field">
              <span>{t('common.nameEn')}</span>
              <input name="nameEn" />
            </label>
            <label className="field">
              <span>{t('common.nameAr')}</span>
              <input name="nameAr" dir="rtl" />
            </label>
            <label className="field">
              <span>{t('machines.manufacturer')}</span>
              <input name="manufacturer" />
            </label>
            <label className="field">
              <span>{t('machines.model')}</span>
              <input name="model" />
            </label>
            <label className="field">
              <span>{t('machines.serial')}</span>
              <input name="serialNumber" />
            </label>
            <label className="field">
              <span>{t('machines.supplier')}</span>
              <input name="supplier" />
            </label>
            <label className="field">
              <span>{t('machines.warrantyEnds')}</span>
              <input name="warrantyEndsOn" type="date" />
            </label>
            <label className="field">
              <span>{t('machines.baselinePulses')}</span>
              <input name="baselinePulses" type="number" min="0" defaultValue="0" />
            </label>
            {register.isError ? <ErrorState error={register.error} /> : null}
            <button type="submit" className="primary" disabled={register.isPending}>
              {t('common.save')}
            </button>
          </form>
        </section>
      ) : null}
    </>
  );
}
